import { TypographyOptions } from '@mui/material/styles/createTypography';

// Define a compatible typography style type
type TypographyStyle = NonNullable<TypographyOptions['h1']>;

// TypeScript interfaces for typography scales
interface DisplayScale {
  large: TypographyStyle;
  medium: TypographyStyle;
  small: TypographyStyle;
}

interface HeadlineScale {
  large: TypographyStyle;
  medium: TypographyStyle;
  small: TypographyStyle;
}

interface TitleScale {
  large: TypographyStyle;
  medium: TypographyStyle;
  small: TypographyStyle;
}

interface LabelScale {
  large: TypographyStyle;
  largeProminent: TypographyStyle;
  medium: TypographyStyle;
  mediumProminent: TypographyStyle;
  small: TypographyStyle;
}

interface BodyScale {
  large: TypographyStyle;
  medium: TypographyStyle;
  mediumProminent: TypographyStyle;
  small: TypographyStyle;
}

interface M3Typography {
  display: DisplayScale;
  headline: HeadlineScale;
  title: TitleScale;
  label: LabelScale;
  body: BodyScale;
}

type TypographySystem = {
  M3: M3Typography;
};

const typography: TypographySystem = {
  M3: {
    display: {
      large: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '57px',
        lineHeight: '64px',
        letterSpacing: '-1.5px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      medium: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '45px',
        lineHeight: '52px',
        letterSpacing: '-1px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      small: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '36px',
        lineHeight: '44px',
        letterSpacing: '-0.5px',
        textDecoration: 'none',
        textTransform: 'none',
      },
    },
    headline: {
      large: {
        fontFamily: 'Archivo',
        fontWeight: 700,
        fontSize: '32px',
        lineHeight: '40px',
        letterSpacing: '0px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      medium: {
        fontFamily: 'Archivo',
        fontWeight: 700,
        fontSize: '28px',
        lineHeight: '36px',
        letterSpacing: '0px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      small: {
        fontFamily: 'Archivo',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '32px',
        letterSpacing: '0px',
        textDecoration: 'none',
        textTransform: 'none',
      },
    },
    title: {
      large: {
        fontFamily: 'Archivo',
        fontWeight: 600,
        fontSize: '22px',
        lineHeight: '28px',
        letterSpacing: '0px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      medium: {
        fontFamily: 'Archivo',
        fontWeight: 700,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0.15px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      small: {
        fontFamily: 'Archivo',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.1px',
        textDecoration: 'none',
        textTransform: 'none',
      },
    },
    label: {
      large: {
        fontFamily: 'Archivo',
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.1px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      largeProminent: {
        fontFamily: 'Archivo',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.1px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      medium: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      mediumProminent: {
        fontFamily: 'Archivo',
        fontWeight: 600,
        fontSize: '12px',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      small: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '11px',
        lineHeight: '16px',
        letterSpacing: '0.5px',
        textDecoration: 'none',
        textTransform: 'none',
      },
    },
    body: {
      large: {
        fontFamily: 'Archivo',
        fontWeight: 400,
        fontSize: '16px',
        lineHeight: '24px',
        letterSpacing: '0px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      medium: {
        fontFamily: 'Archivo',
        fontWeight: 400,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.25px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      mediumProminent: {
        fontFamily: 'Archivo',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.25px',
        textDecoration: 'none',
        textTransform: 'none',
      },
      small: {
        fontFamily: 'Archivo',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '16px',
        letterSpacing: '0.4px',
        textDecoration: 'none',
        textTransform: 'none',
      },
    },
  },
};

export type { TypographySystem };
export default typography;
